import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import {
    CardCatalogueDataSource,
    CardContainer,
    EMPTY_LINK,
    ICardComponent,
    ILink,
    ImageData,
    LinkDirective,
    LinkService,
    MediaImage,
    SnowplowService,
    withConfigFallback,
} from '@sirius/common';
import { ICardContent } from 'shared/models';

@Component({
    selector: 'commerce-card-content',
    templateUrl: './card-content.component.html',
    styleUrl: './card-content.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LinkDirective],
})
export class CardContentComponent implements ICardComponent<ICardContent> {
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);

    readonly config = input<ICardContent | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    // Public inputs keep their natural API name. Consumers bind `[title]`, `[link]`, etc.
    readonly title = input('');
    readonly link = input<ILink>(EMPTY_LINK);
    readonly image = input<MediaImage | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);

    // Internal derived signals read by this component's own template only.
    protected readonly resolvedTitle = withConfigFallback<ICardContent, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedLink = withConfigFallback<ICardContent, 'link', ILink>(this.config, 'link', this.link);
    protected readonly resolvedImage = withConfigFallback<ICardContent, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedImageData = withConfigFallback<ICardContent, 'imageData'>(this.config, 'imageData', this.imageData);

    readonly selectEvent = output<void>();

    onClick(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const link = this.resolvedLink();

        this._snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this._linkService.getFullPath(link.href),
            outbound: link.target === '_blank',
            element_content: link.title,
            context: [],
        });
    }
}
